import moment from 'moment'
import { RRule, datetime } from 'rrule'

/**
 * icalendar data 의 vtodo model
 *  ui 에서 사용 하며, 서버로 올릴때 ics 로 변환 하여 올린다.
 */
export interface TodoItem {
  url: string // url - vtodo 의 url ex) https://mail.officemail.app/dav/calendars/user․41ad4/6f1a0325-324b-4880-a2fd-30a63b5d3ea3.ics
  uid: string // uid - serverId ex) 0b2f2072-b2f0-47c9-8b72-c68af81ff275
  folderUrl: string // folder url
  etag: string
  syncState?: TodoSyncState // 동기화 상태
  summary: string
  description: string | null
  status: TodoStatus
  priority: Priority | null // default Priority.MEDIUM
  percentComplete: number // default 0
  startDate: Date | null
  dueDate: Date | null
  dtstamp: Date | null
  createdDate: Date | null
  lastModifiedDate: Date | null
  location?: string | null
  completedDate?: string | Date | null
  recurrenceRule?: RRule | null
  recurrenceRuleString?: string | null // DB 저장용 RRule 문자열
  recurrenceId?: Date | null // RECURRENCE-ID for specific instance of recurring todo
  // 기타 VTODO 속성들...
  datatype: string | null
  datetype: string | null // 'date' or 'datetime', default 'date'
  alarms: TaskReminder[] | null
  author?: { val: string; params: { CN: string } } | null
  unknownProperties: { key: string; value: unknown }[] | null
}

/**
 * RRule 객체를 IndexedDB 저장용 문자열로 변환
 * @param rrule RRule 객체
 * @returns RRule 문자열 (RRULE: 제외)
 */
export const rruleToString = (rrule: RRule | null): string | null => {
  if (!rrule) return null
  try {
    const rrulStrArr = rrule.toString().split('\n')
    const rrulStr = rrulStrArr.find((str) => str.startsWith('RRULE:'))

    return rrulStr.replace('RRULE:', '')
  } catch (error) {
    console.error('Error converting RRule to string:', error)
    return null
  }
}

/**
 * 문자열을 RRule 객체로 변환
 * @param rruleString RRule 문자열 (RRULE: 제외)
 * @returns RRule 객체
 */
export const stringToRRule = (rruleString: string | null, dtstart?: Date | null): RRule | null => {
  if (!rruleString) return null
  try {
    const rrule = RRule.fromString(`RRULE:${rruleString}`)
    if (dtstart) {
      const startDate = new Date(dtstart)
      if (!isNaN(startDate.getTime())) {
        const options = { ...rrule.origOptions }
        options.dtstart = datetime(
          startDate.getFullYear(),
          startDate.getMonth() + 1,
          startDate.getDate(),
          startDate.getHours(),
          startDate.getMinutes(),
          startDate.getSeconds(),
        )
        return new RRule(options)
      }
    }
    return rrule
  } catch (error) {
    console.error('Error converting string to RRule:', error)
    return null
  }
}

/**
 * TodoItem을 DB 저장용으로 변환 (RRule → string)
 * @param todo TodoItem 객체
 * @returns DB 저장용 TodoItem
 */
export const todoItemForDB = (
  todo: TodoItem,
): Omit<TodoItem, 'recurrenceRule'> & { recurrenceRuleString: string | null } => {
  const { recurrenceRule, ...rest } = todo
  return {
    ...rest,
    recurrenceRuleString: rruleToString(recurrenceRule),
  }
}

/**
 * DB에서 가져온 데이터를 TodoItem으로 변환 (string → RRule)
 * @param todoData DB에서 가져온 데이터
 * @returns TodoItem 객체
 */
export type TodoItemDBRecord = Omit<TodoItem, 'recurrenceRule'> & {
  recurrenceRuleString?: string | null
}

export const todoItemFromDB = (todoData: TodoItemDBRecord): TodoItem => {
  const dtstart = todoData.startDate || todoData.dueDate
  return {
    ...todoData,
    recurrenceRule: stringToRRule(
      todoData.recurrenceRuleString ?? null,
      dtstart ? new Date(dtstart as string | number | Date) : null,
    ),
  }
}

/**
 * Todo 동기화 상태
 * - SERVER: 서버와 동기화된 상태
 * - LOCAL_DIRTY: 로컬에서 수정되어 서버 동기화가 필요한 상태
 * - SOFT_DELETED: 소프트 삭제 상태 (추후 undo 기능용, 현재 미사용)
 */
export const syncState = {
  NONE: 'NONE',
  SERVER: 'SERVER',
  LOCAL_DIRTY: 'LOCAL_DIRTY',
  SOFT_DELETED: 'SOFT_DELETED',
} as const

export type TodoSyncState = (typeof syncState)[keyof typeof syncState]

export const PRIORITY = {
  HIGH: 'HIGH', // 1: HIGH
  MEDIUM: 'MEDIUM', // 5: MEDIUM
  LOW: 'LOW', // 9: LOW
} as const

export type Priority = (typeof PRIORITY)[keyof typeof PRIORITY]

// ICS priority number(1, 5, 9) -> Priority 타입('1', '5', '9')로 변환
export const priorityNumberToPriority = (priority: string): Priority => {
  if (priority === '1') return PRIORITY.HIGH
  if (priority === '9') return PRIORITY.LOW
  return PRIORITY.MEDIUM // 기본값
}

// Priority 타입('1', '5', '9') -> ICS number로 변환
export const priorityToNumber = (priority: Priority): string => {
  if (priority === PRIORITY.HIGH) return '1'
  if (priority === PRIORITY.MEDIUM) return '5'
  if (priority === PRIORITY.LOW) return '9'
  return '5' // 기본값
}

export const TODO_STATUS = {
  NEEDS_ACTION: 'NEEDS-ACTION',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const

export type TodoStatus = (typeof TODO_STATUS)[keyof typeof TODO_STATUS]

// todo_status 의 값을 받아서 TODO_STATUS 로 변환되는 api 추가.
export const todoStatusToStatus = (todoStatus: string): TodoStatus => {
  return TODO_STATUS[todoStatus as keyof typeof TODO_STATUS] || TODO_STATUS.NEEDS_ACTION
}

export type TaskReminder =
  | {
      // 상대 시간 - 기준 시각 10분 전 (Due or Start 기준)
      action: string
      end?: Date // Wed Jul 09 2025 16:07:32 GMT+0900 (Korean Standard Time)
      trigger: string // '-PT15M' 등
    }
  | {
      // 절대 시간 - 고정된 UTC 시간
      action: string // 'DISPLAY'
      end?: Date // Wed Jul 09 2025 16:07:32 GMT+0900 (Korean Standard Time)
      trigger: {
        params: Record<string, string>
        val: string // '20250708T080000Z' 등
      }
      uid?: string // '146A72A2-5CF7-42E2-B77F-C46991B20455'
    }

// - DTSTART가 있으면 → 항상 DTSTART 기준
// - DTSTART가 없고 DUE만 있으면 → DUE 기준
// - reminder.trigger.val 이 있으면 → reminder.trigger.val 기준 (date & time)
// - 둘 다 없으면 → 알림 설정은 의미 없음
export function getReminderTime(
  reminder: TaskReminder,
  dueDateStr?: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startDate?: Date,
): Date | null {
  if (typeof reminder.trigger === 'string' && reminder.trigger.startsWith('-')) {
    // 상대 시간

    if (dueDateStr) {
      const dueDate = moment(dueDateStr).toDate()
      const seconds = secondsDuration(reminder.trigger)
      return new Date(dueDate.getTime() + seconds * 1000)
    } else {
      return null
    }
  }

  // 절대 시간 - trigger가 객체인 경우에만 val 속성 접근
  if (typeof reminder.trigger === 'object' && reminder.trigger.val) {
    const alarmTime = moment(reminder.trigger.val).toDate()
    return new Date(alarmTime)
  }

  return null
}

/**
 * ISO 8601 Duration 형식의 문자열을 초 단위로 변환하는 함수
 *
 * @param durStr - ISO 8601 Duration 형식의 문자열 (예: "PT15M", "-PT2H30M", "P1DT2H30M")
 * @returns 초 단위의 시간 값 (음수일 수 있음)
 *
 * @example
 * secondsDuration("PT15M")     // 900초 (15분)
 * secondsDuration("-PT2H30M")  // -9000초 (-2시간 30분)
 * secondsDuration("P1DT2H30M") // 95400초 (1일 2시간 30분)
 *
 * @description
 * ISO 8601 Duration 형식 규칙:
 * - P: Period (기간)를 나타내는 접두사
 * - T: Time (시간)를 나타내는 구분자
 * - D: Day (일)
 * - H: Hour (시간)
 * - M: Minute (분)
 * - 음수는 문자열 앞에 '-'로 표시
 */
export function secondsDuration(durStr: string): number {
  let seconds = 0

  // 빈 문자열이면 0 반환
  if (durStr.length > 0) {
    // 음수 부호 처리
    let minus = true
    const sign = durStr.substring(0, 1)
    if (sign !== '-') {
      minus = false
    }

    let min = 0 // 분 단위로 누적 계산
    let digitStr = '' // 숫자 문자열을 임시 저장

    // 문자열을 한 글자씩 파싱
    for (let index = 0; index < durStr.length; index++) {
      const ch = durStr.charAt(index)

      // 숫자나 공백이면 digitStr에 추가
      if (ch.trim().length === 0 || !isNaN(parseInt(ch))) {
        digitStr += ch
      }
      // 단위 문자(D, H, M)가 나오면 이전 숫자를 처리
      else if (digitStr.length > 0) {
        const digit = parseInt(digitStr)
        digitStr = '' // 다음 단위를 위해 초기화

        // 단위별로 분 단위로 변환하여 누적
        if (ch === 'D') {
          min += 60 * 24 * digit // 일 → 분 (1일 = 24시간 = 1440분)
        } else if (ch === 'H') {
          min += 60 * digit // 시간 → 분 (1시간 = 60분)
        } else if (ch === 'M') {
          min += digit // 분 → 분 (그대로)
        }
      }
    }

    // 음수 부호 적용
    min *= minus ? -1 : 1

    // 분을 초로 변환 (1분 = 60초)
    seconds = min * 60
  }

  return seconds
}
